import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const formSchema = z.object({
    newPassword: z.string()
        .min(8, 'Şifre en az 8 karakter olmalıdır')
        .regex(/[A-Z]/, 'Şifre en az bir büyük harf içermelidir')
        .regex(/[0-9]/, 'Şifre en az bir rakam içermelidir'),
    confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Şifreler eşleşmiyor',
    path: ['confirmPassword'],
});

type FormValues = z.infer<typeof formSchema>;

export function InitialPasswordChangeDialog() {
    const { user, accessToken, setUser } = useAuthStore((s) => s.auth);
    const [isLoading, setIsLoading] = useState(false);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            newPassword: '',
            confirmPassword: '',
        },
    });

    const isOpen = user?.requiresPasswordChange === true;

    async function onSubmit(data: FormValues) {
        setIsLoading(true);
        try {
            const response = await axios.post(
                `${API_URL}/auth/change-initial-password`,
                { newPassword: data.newPassword },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            // Update local storage and zustand state natively
            if (user) {
                setUser({
                    ...user,
                    requiresPasswordChange: false
                });
            }

            toast.success(response.data.message || 'Şifreniz başarıyla güncellendi. Yönlendiriliyorsunuz...');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Bir hata oluştu. Lütfen şifrenizi kontrol edip tekrar deneyin.');
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <AlertDialog open={isOpen} onOpenChange={(open) => {
            // Forcefully prevent closing by clicking outside or pressing escape
            if (!open) return;
        }}>
            <AlertDialogContent className="sm:max-w-[425px]">
                <AlertDialogHeader>
                    <AlertDialogTitle>Güvenlik Uyarısı: Şifre Değişikliği Gerekli</AlertDialogTitle>
                    <AlertDialogDescription>
                        Sistem yöneticisi tarafından oluşturulan hesabınıza ilk kez giriş yapıyorsunuz. Güvenliğiniz için lütfen yeni bir şifre belirleyin.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
                        <FormField
                            control={form.control}
                            name="newPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Yeni Şifre</FormLabel>
                                    <FormControl>
                                        <Input type="password" placeholder="••••••••" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="confirmPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Yeni Şifre (Tekrar)</FormLabel>
                                    <FormControl>
                                        <Input type="password" placeholder="••••••••" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="flex justify-end pt-4">
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? 'Güncelleniyor...' : 'Şifremi Değiştir ve Devam Et'}
                            </Button>
                        </div>
                    </form>
                </Form>
            </AlertDialogContent>
        </AlertDialog>
    );
}
